import { Observable, forkJoin } from 'rxjs';
import { map, take, mergeMap } from 'rxjs/operators';
import { ContentParser } from './content.parser';
import { ContentConfig } from './content.config';
import { ContentService } from './content.service';
import { isNullOrEmpty } from '@awp/core/utils';
import * as i0 from "@angular/core";
import * as i1 from "./content.config";
import * as i2 from "./content.service";
import * as i3 from "./content.parser";
var ContentProvider = /** @class */ (function () {
    /**
     * @param config An instance of the configuration specifying the current lang
     * @param store An instance of the store for all retrieved keys
     * @param parser An instance of the parser currently used
     */
    function ContentProvider(config, contentService, parser) {
        this.config = config;
        this.contentService = contentService;
        this.parser = parser;
        this.onLangChanged = config.onCurrentLangChange;
    }
    /**
     * Gets the translated value of a key (or an array of keys)
     * @returns the translated key, or an object of translated keys
     */
    ContentProvider.prototype.get = function (key, interpolateParams, rootContent) {
        var _this = this;
        if (key == null || !key.length) {
            throw new Error("Parameter \"key\" required");
        }
        return Observable.create(function (observer) {
            var onComplete = function (res) {
                observer.next(res);
                observer.complete();
            };
            var onError = function (err) {
                observer.error(err);
            };
            var parseContentResult = function (target, subKey, interpolateParams) {
                var result = _this.getParsedResult(target, subKey, interpolateParams);
                if (result != null && typeof result.subscribe === "function") {
                    result.pipe(take(1))
                        .subscribe(function (data) {
                        parseContentResult(data.target, data.key, interpolateParams);
                    }, onError);
                }
                else {
                    onComplete(result);
                }
            };
            if (rootContent == null) {
                _this.fetchRoot()
                    .pipe(take(1))
                    .subscribe(function (content) {
                    parseContentResult(content, key, interpolateParams);
                }, onError);
            }
            else {
                parseContentResult(rootContent, key, interpolateParams);
            }
        });
    };
    ContentProvider.prototype.getRawValue = function (key) {
        var _this = this;
        if (key == null || !key.length) {
            throw new Error("Parameter \"key\" required");
        }
        return Observable.create(function (observer) {
            var onComplete = function (res) {
                observer.next(res);
                observer.complete();
            };
            var onError = function (err) {
                observer.error(err);
            };
            var getRawValue = function (target, subKey) {
                var result = _this._getKeyValue(target, subKey);
                if (result != null && typeof result.subscribe === "function") {
                    result.pipe(take(1))
                        .subscribe(function (data) {
                        if (typeof data === 'string') {
                            onComplete(data);
                        }
                        else {
                            getRawValue(data.target, data.key);
                        }
                    }, onError);
                }
                else {
                    onComplete(result);
                }
            };
            _this.fetchRoot()
                .pipe(take(1))
                .subscribe(function (content) {
                getRawValue(content, key);
            }, onError);
        });
    };
    /**
     * Returns the raw value result of the content
     */
    ContentProvider.prototype._getKeyValue = function (content, key) {
        if (typeof content === 'string' || isNullOrEmpty(key)) {
            return content;
        }
        var result;
        var value = this.getValue(content, key);
        if (value.link != null) {
            return this.fetchContent(value.link.href, value.link.type)
                .pipe(map(function (p) {
                return {
                    key: value.keys,
                    target: p
                };
            }));
        }
        if (value.target != null) {
            if (typeof value.target === 'string') {
                result = value.target;
                return result != null ? result : key;
            }
            else if (value.target.href != null && value.target.type === 'html') {
                return this.fetchContent(value.target.href, value.target.type);
            }
            else {
                return value.target;
            }
        }
        return undefined;
    };
    /**
     * Returns the parsed result of the content
     */
    ContentProvider.prototype.getParsedResult = function (content, key, interpolateParams) {
        if (typeof content === 'string') {
            return this.parserInterpolate(content, interpolateParams);
        }
        if (isNullOrEmpty(key)) {
            return content;
        }
        var result;
        var value = this.getValue(content, key);
        if (value.link != null) {
            return this.fetchContent(value.link.href, value.link.type)
                .pipe(map(function (p) {
                return {
                    key: value.keys,
                    target: p
                };
            }));
        }
        if (value.target != null) {
            if (typeof value.target === 'string') {
                result = this.parserInterpolate(value.target, interpolateParams);
                return result != null ? result : key;
            }
            else {
                return value.target;
            }
        }
        return undefined;
    };
    ContentProvider.prototype.parserInterpolate = function (content, params) {
        var result = this.parser.interpolate(content, params);
        return result;
    };
    ContentProvider.prototype.fetchRoot = function () {
        return this.fetchContent('root');
    };
    ContentProvider.prototype.fetchContent = function (url, type) {
        if (type === void 0) { type = 'json'; }
        return this.contentService.getContent(url, this.config.currentLang, type).pipe(take(1));
    };
    ContentProvider.prototype.preFetchContent = function (keys) {
        var _this = this;
        return this.fetchRoot()
            .pipe(mergeMap(function (content) {
            var urls = keys.map(function (key) {
                var value = _this.getValue(content, key);
                return value != null && value.link != null ? value.link.href : null;
            }).filter(Boolean);
            var o = urls.map(function (url) { return _this.fetchContent(url); });
            return forkJoin(o);
        }), take(1));
    };
    ContentProvider.prototype.getValue = function (target, key) {
        var keys = key.split('.');
        var itemKey = '';
        do {
            itemKey += keys.shift();
            if (target != null && target[itemKey] != null && (typeof target[itemKey] === 'object' || !keys.length)) {
                target = target[itemKey];
                itemKey = '';
            }
            else if (target != null) {
                var link = target._links != null ? target._links[itemKey] : null;
                if (link != null) {
                    return { link: link, keys: keys.join('.') };
                }
                var embedded = target._embedded != null ? target._embedded[itemKey] : null;
                if (embedded) {
                    target = embedded;
                    itemKey = '';
                }
                else {
                    target = undefined;
                    itemKey = '';
                    break;
                }
            }
            else {
                target = undefined;
                itemKey = '';
                break;
            }
        } while (keys.length);
        return { target: target, keys: keys.join('.') };
    };
    ContentProvider.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function ContentProvider_Factory() { return new ContentProvider(i0.ɵɵinject(i1.ContentConfig), i0.ɵɵinject(i2.ContentService), i0.ɵɵinject(i3.ContentParser)); }, token: ContentProvider, providedIn: "root" });
    return ContentProvider;
}());
export { ContentProvider };
